import React, { useEffect, useState } from "react"
import {
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap"
import { integrationManagement } from "helpers/utils"
import editIcon from "../../assets/images/icons/edit.svg"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  useLazyGetStoreConfigsQuery,
  useStoreSystemConfigMutation,
} from "store/api/platformManager/platformManager.api"
import toast from "react-hot-toast"
import Spinners from "components/Common/Spinner"
import LoaderComponent from "components/loader"

const getDefaultData = (data) => {
  const configs = data || integrationManagement

  return configs.reduce((prev, item) => {
    if (item.type === "FIELD") {
      prev[item.key] = item.value || ""
    }
    const { type, ...values } = prev
    return values
  }, {})
}

const IntegrationManagement = () => {
  const [mutate, { isLoading }] = useStoreSystemConfigMutation()
  const [trigger, { isFetching }] = useLazyGetStoreConfigsQuery()
  const [ids, setIds] = useState([])
  const [systemConfigs, setSystemConfigs] = useState({
    initialData: getDefaultData(),
    configs: integrationManagement,
  })
  document.title = "Integration Management"

  const handleTransformedData = (data) => {
    const transformedData = integrationManagement.map((item) => {
      const found = data.find((el) => el.key === item.key)

      if (found) {
        return {
          ...found,
          helper_text: item.helper_text,
          ...(item.options ? { options: item.options } : {}),
        }
      } else {
        return item
      }
    })

    setSystemConfigs({
      initialData: getDefaultData(transformedData),
      configs: transformedData,
    })
  }

  const getStoreConfigs = async () => {
    try {
      const { data, message, success } = await trigger().unwrap()
      if (success) {
        if (data.length > 0) {
          handleTransformedData(data)
        }
      }
    } catch (err) {}
  }

  useEffect(() => {
    getStoreConfigs()
  }, [])

  const storeSystemConfig = async (body) => {
    const systemConfig = Object.keys(body).map((item) => {
      const foundItem = integrationManagement.find((el) => el.key === item)

      const { options, ...rest } = foundItem
      return {
        ...rest,
        value: body[item],
        helper_text: "",
      }
    })

    try {
      const { success, message } = await mutate({
        systemConfig,
      }).unwrap()
      if (success) {
        toast.success(message, { id: "storeConfigSuccess" })
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const formikErrors = data.errors.reduce((acc, curr) => {
          acc[curr.key] = curr.message
          return acc
        }, {})
        validation.setErrors(formikErrors)
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: systemConfigs.initialData,
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      storeSystemConfig(values)
    },
  })

  const onHandleDisableField = (key) => {
    setIds((prev) => [...prev, key])
  }

  const checkIfDisabled = (key) => {
    return !ids.includes(key)
  }

  return (
    <>
      <div className="page-content">
        {isFetching ? (
          <LoaderComponent />
        ) : (
          <Container fluid>
            <Row>
              <Col xl={12}>
                <div className="platform-heading-section mb-4 pt-4">
                  <h4>Integration Management</h4>
                </div>
              </Col>
            </Row>
            <Form
              onSubmit={(e) => {
                e.preventDefault()
                validation.handleSubmit()
                return false
              }}
            >
              {systemConfigs.configs.map((item, index) => (
                <React.Fragment key={`integration_${index}`}>
                  <>
                    {item.type === "HEADING" ? (
                      <>
                        <div className="py-3" />
                        <h5 className="mb-4">{item.label}</h5>
                      </>
                    ) : (
                      <FormGroup row className="mb-4 ps-4">
                        <Label className="form-label" md={4}>
                          {item.label}
                        </Label>
                        <Col md={6}>
                          {item.options && item.options.length > 0 ? (
                            <div className="input-wrapper">
                              <select
                                id={item.key}
                                name={item.key}
                                disabled={checkIfDisabled(item.key)}
                                className={`form-select pageSize ${
                                  !!(
                                    validation.touched[item.key] &&
                                    validation.errors[item.key]
                                  ) && "is-invalid"
                                } ${
                                  checkIfDisabled(item.key) ? "with-icon" : ""
                                }`}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values[item.key]}
                              >
                                <option value={""}>Select</option>
                                {item.options.map((option, index) => (
                                  <option
                                    key={`options_${index}`}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              {checkIfDisabled(item.key) && (
                                <div
                                  onClick={() => onHandleDisableField(item.key)}
                                >
                                  <img className="cp" src={editIcon} alt="" />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="input-wrapper">
                              <Input
                                id={item.key}
                                name={item.key}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values[item.key]}
                                placeholder={`Enter ${item.label}`}
                                type="text"
                                disabled={checkIfDisabled(item.key)}
                                className="form-control"
                                invalid={
                                  !!(
                                    validation.touched[item.key] &&
                                    validation.errors[item.key]
                                  )
                                }
                              />
                              {checkIfDisabled(item.key) && (
                                <div
                                  onClick={() => onHandleDisableField(item.key)}
                                >
                                  <img className="cp" src={editIcon} alt="" />
                                </div>
                              )}
                            </div>
                          )}
                          {item?.helper_text && (
                            <span className="text-muted">
                              {item?.helper_text}
                            </span>
                          )}
                          {validation.touched[item.key] &&
                          validation.errors[item.key] ? (
                            <FormFeedback type="invalid">
                              {validation.errors[item.key]}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </FormGroup>
                    )}
                  </>
                </React.Fragment>
              ))}

              <Row className="justify-content-end my-4">
                <hr />
                <Col sm={12}>
                  <div className="d-flex justify-content-end align-items-center gap-3">
                    <button
                      type="submit"
                      className="btn btn-primary w-md"
                      disabled={isLoading}
                    >
                      {isLoading ? <Spinners /> : "Save Changes"}
                    </button>
                  </div>
                </Col>
              </Row>
            </Form>
          </Container>
        )}
      </div>
    </>
  )
}

export default IntegrationManagement
